// src/hooks/useDigitalTwin.js
// --- UPDATE: v2.0 — performance + correctness for large networks
//   RENDER LOOP
//   • Sensor → asset/pipeline link propagation searched the asset group with
//     children.find() for EVERY sensor on EVERY frame (O(sensors × assets) at
//     60 fps). It now uses id→object Maps and only re-evaluates colours/links
//     when telemetry actually changes (readings version); per-frame work is
//     limited to pulsing the critical sensors.
//   • Per-frame allocations (2× Matrix4, Object3D, Color) hoisted.
//   • Optional on-demand rendering: initialize(cb, { renderOnDemand: true })
//     stops the forced 60 fps repaint when nothing is animating. Default keeps
//     the previous continuous behaviour.
//   • Objects flagged by a simulation (userData.simulationState) keep their
//     simulated glow — live link propagation no longer overrides it.
//   CORRECTNESS
//   • Map resize called renderer.setSize(canvas.width, …) which also set the CSS
//     size to DEVICE pixels — on HiDPI screens that doubled the shared map
//     canvas. Now only the three.js viewport is updated.
//   • Sensors without coordinates kept identity matrices → phantom spheres at
//     the scene origin. They are excluded from the instanced mesh.
//   • clearSelection() never cleared a selected SENSOR (instanced picks have no
//     highlighted object), so the Inspect panel stayed on it.
//   • Click/hover no longer mutate the source data objects (isSensor/isAsset
//     were written into React state arrays); hover only updates state when the
//     hovered item changes (was up to 20 page re-renders/s while moving).
//   • populateScene loads models with bounded concurrency (was strictly
//     sequential) while keeping the stale-populate guard.
//   • AR reparents the sensors and simulation layers too.
//   • `map` is real state (was a ref snapshot inside useMemo).
//   NEW API: requestRender(), metersToScene(), getAssetObject(id)
// --- END UPDATE
import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import * as THREE from 'three';
import { MercatorCoordinate } from 'mapbox-gl';
import modelLoaderService from '../services/modelLoaderService';
import animationService from '../services/animationService';
import { config } from '../config';

// ==================== DEBUG SYSTEM ====================
const logger = {
  log: (...args) => console.log('[DT_HOOK]', ...args),
  warn: (...args) => console.warn('[DT_HOOK]', ...args),
  error: (...args) => console.error('[DT_HOOK]', ...args),
  verbose: false,
};

mapboxgl.accessToken = config.mapboxToken;

// ==================== CONSTANTS ====================
const SELECTION_COLOR = 0xffaa00;
const HOVER_COLOR = 0x00aaff;
const HOVER_SCALE = 1.06;
const SELECT_SCALE = 1.08;
const ASSET_SCALE_MULTIPLIER = 5;
const MOUSE_THROTTLE_MS = 50;
const MAX_PARENT_DEPTH = 10;
// --- UPDATE
const MODEL_LOAD_CONCURRENCY = 8;
// --- END UPDATE

// AR hologram world-space constants — tune per deployment coordinate scale
const AR_HOLOGRAM_SCALE    = 0.0002;
const AR_HOLOGRAM_POSITION = { x: 0, y: -0.5, z: -1.5 }; // 1.5 m ahead, 0.5 m below eye
const AR_HOLOGRAM_ROTATION_X = -Math.PI / 2; // Lay flat like a tabletop map

// Names of the scene groups that must be reparented into / out of AR
// --- UPDATE: sensors and simulation overlay were left behind in Mapbox space
const AR_REPARENTED_GROUPS = ['assets', 'pipelines', 'sensors', 'anomalies', 'predictedFailures', 'simulationImpact'];
// --- END UPDATE

const statusColors = {
  operational:          new THREE.Color(0x00dd00),
  active:               new THREE.Color(0x00dd00),
  online:               new THREE.Color(0x00dd00),
  good:                 new THREE.Color(0x00dd00),
  normal:               new THREE.Color(0x00dd00),
  maintenance_required: new THREE.Color(0xffa500),
  maintenance:          new THREE.Color(0xffa500),
  warning:              new THREE.Color(0xffa500),
  damaged:              new THREE.Color(0xff0000),
  critical:             new THREE.Color(0xff0000),
  offline:              new THREE.Color(0xff0000),
  decommissioned:       new THREE.Color(0x808080),
  inactive:             new THREE.Color(0x808080),
  unknown:              new THREE.Color(0xaaaaaa),
};

const healthStatusColors = {
  good:    new THREE.Color(0x00dd00),
  normal:  new THREE.Color(0x00dd00),
  warning: new THREE.Color(0xffa500),
  critical: new THREE.Color(0xff0000),
  unknown:  new THREE.Color(0xaaaaaa),
};

// ==================== ANIMATION HELPERS ====================
const addPulsingAnimation = (mesh) => {
  const initialScale = mesh.scale.clone();
  const pulseScale = initialScale.clone().multiplyScalar(1.2);

  const scaleValues = [
    initialScale.x, initialScale.y, initialScale.z,
    pulseScale.x,   pulseScale.y,   pulseScale.z,
    initialScale.x, initialScale.y, initialScale.z,
  ];
  const times = [0, 0.5, 1.0];

  const track  = new THREE.VectorKeyframeTrack('.scale', times, scaleValues);
  const clip   = new THREE.AnimationClip('pulse', -1, [track]);
  const mixer  = new THREE.AnimationMixer(mesh);
  const action = mixer.clipAction(clip);
  action.setLoop(THREE.LoopRepeat);
  action.play();
  mesh.userData.animationMixer = mixer;
  animationService.add(mesh);
};

// ==================== EMISSIVE MANAGEMENT ====================
const emissiveStateMap = new WeakMap();

const applyEmissive = (object3D, colorHex) => {
  object3D.traverse(node => {
    if (node.isMesh && node.material) {
      const processMat = (mat) => {
        if (!emissiveStateMap.has(mat)) {
          emissiveStateMap.set(mat, {
            emissive:  mat.emissive?.getHex ? mat.emissive.getHex() : 0x000000,
            intensity: mat.emissiveIntensity ?? 0,
          });
        }
        if (mat.emissive) {
          const original = emissiveStateMap.get(mat);
          const targetIntensity = Math.max(original.intensity, 0.8);
          mat.emissive.setHex(colorHex);
          mat.emissiveIntensity = targetIntensity;
        }
      };
      if (Array.isArray(node.material)) node.material.forEach(processMat);
      else processMat(node.material);
    }
  });
};

const restoreEmissive = (object3D) => {
  object3D.traverse(node => {
    if (node.isMesh && node.material) {
      const processMat = (mat) => {
        if (emissiveStateMap.has(mat)) {
          const original = emissiveStateMap.get(mat);
          if (mat.emissive) {
            mat.emissive.setHex(original.emissive);
            mat.emissiveIntensity = original.intensity;
          }
        }
      };
      if (Array.isArray(node.material)) node.material.forEach(processMat);
      else processMat(node.material);
    }
  });
};

const clearEmissiveState = (object3D) => {
  object3D.traverse(node => {
    if (node.isMesh && node.material) {
      if (Array.isArray(node.material)) node.material.forEach(m => emissiveStateMap.delete(m));
      else emissiveStateMap.delete(node.material);
    }
  });
};

const disposeObject = (obj) => {
  if (!obj) return;
  obj.traverse(child => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach(m => { if (m.map) m.map.dispose(); m.dispose(); });
      } else {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    }
  });
  clearEmissiveState(obj);
  if (obj.userData.animationMixer) {
    animationService.remove(obj);
    obj.userData.animationMixer = null;
  }
};

// ==================== MAIN HOOK ====================
export function useDigitalTwin(containerRef) {
  const mapRef  = useRef(null);
  const threeRef = useRef({
    renderer:       null,
    scene:          null,
    camera:         null,
    assetsGroup:    null,
    pipelinesGroup: null,
    sensorsGroup:   null,
    origin:         null,
  });

  // Ref to track live WebSocket data without triggering React re-renders
  const liveReadingsRef = useRef({});
  const sensorLinksRef = useRef({}); // <-- NEW: Tracks link relationships

  const [scene,         setScene]         = useState(null);
  const [origin,        setOrigin]        = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [hoveredAsset,  setHoveredAsset]  = useState(null);
  const [isReady,       setIsReady]       = useState(false);
  // --- UPDATE: real map state (the memo previously captured mapRef.current once)
  const [mapInstance,   setMapInstance]   = useState(null);
  // --- END UPDATE
  // --- UPDATE (twin v8): progressive scene build — { total, loaded, failed, done, startedAt, ms }
  const [sceneProgress, setSceneProgress] = useState({ total: 0, loaded: 0, failed: 0, done: false, ms: null });
  // --- END UPDATE

  const isMountedRef          = useRef(true);
  const currentPopulateIdRef  = useRef(0);
  const highlightedObjectRef  = useRef(null);
  const hoveredObjectRef      = useRef(null);
  const lastMouseMoveRef      = useRef(0);
  const raycastCacheRef       = useRef([]);

  // Track whether an AR session is currently active so the render loop
  // and Mapbox teardown can skip/adjust their paths safely.
  const arSessionActiveRef    = useRef(false);

  // --- UPDATE: render-loop bookkeeping
  const readingsVersionRef    = useRef(0);   // bumps on every telemetry update
  const appliedVersionRef     = useRef(-1);  // version last applied to colours/links
  const criticalSensorIdxRef  = useRef([]);  // instance indices currently pulsing
  const assetObjectsRef       = useRef(new Map()); // assetId -> Object3D
  const pipelineObjectsRef    = useRef(null);      // pipelineId -> Object3D (lazy)
  const pulsingCountRef       = useRef(0);
  const renderOnDemandRef     = useRef(false);
  const hoveredKeyRef         = useRef(null);
  const frameScratch = useRef({
    m: new THREE.Matrix4(),
    t: new THREE.Matrix4(),
    dummy: new THREE.Object3D(),
    color: new THREE.Color(),
  });
  // --- END UPDATE

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Fast updater — feeds the 3D render loop directly, no React re-render
  const updateLiveReadings = useCallback((readings, links = null) => {
    liveReadingsRef.current = readings || {};
    if (links) sensorLinksRef.current = links;
    // --- UPDATE: tell the render loop to re-evaluate colours / links once
    readingsVersionRef.current += 1;
    mapRef.current?.triggerRepaint?.();
    // --- END UPDATE
  }, []);

  // --- UPDATE: small public helpers
  const requestRender = useCallback(() => { mapRef.current?.triggerRepaint?.(); }, []);
  const metersToScene = useCallback((meters = 1) => {
    const o = threeRef.current.origin;
    return o ? meters * o.meterInMercatorCoordinateUnits() : meters;
  }, []);
  const getAssetObject = useCallback((id) => assetObjectsRef.current.get(String(id)) || null, []);
  // --- END UPDATE

  const ensureSceneGroups = useCallback(() => {
    const three = threeRef.current;
    if (!three.scene) return;
    ['assets', 'pipelines', 'sensors'].forEach(name => {
      if (!three.scene.getObjectByName(name)) {
        const group = new THREE.Group();
        group.name = name;
        three.scene.add(group);
      }
    });
    three.assetsGroup    = three.scene.getObjectByName('assets');
    three.pipelinesGroup = three.scene.getObjectByName('pipelines');
    three.sensorsGroup   = three.scene.getObjectByName('sensors');
  }, []);

  const updateRaycastCache = useCallback(() => {
    const three = threeRef.current;
    if (!three.assetsGroup || !three.sensorsGroup || !three.pipelinesGroup) return;
    raycastCacheRef.current = [
      ...three.assetsGroup.children,
      ...three.sensorsGroup.children,
      ...three.pipelinesGroup.children,
    ];
    // --- UPDATE: pipelines are drawn by Pipeline3DLayer — rebuild their lookup lazily
    pipelineObjectsRef.current = null;
    appliedVersionRef.current = -1;
    // --- END UPDATE
  }, []);

  const findRoot = useCallback((object) => {
    let cur = object, depth = 0;
    while (cur) {
      if (depth++ > MAX_PARENT_DEPTH) return null;
      if (cur.userData?.assetId)    return cur;
      if (cur.userData?.sensorId)   return cur;
      if (cur.userData?.pipelineId) return cur;
      cur = cur.parent;
    }
    return null;
  }, []);

  const clearSelection = useCallback(() => {
    if (highlightedObjectRef.current) {
      if (!highlightedObjectRef.current.isInstancedMesh) {
        const baseScale = highlightedObjectRef.current.userData.originalScale || 1;
        highlightedObjectRef.current.scale.setScalar(baseScale);
        restoreEmissive(highlightedObjectRef.current);
      }
      highlightedObjectRef.current = null;
    }
    // --- UPDATE: also clears an instanced (sensor) selection
    setSelectedAsset(null);
    mapRef.current?.triggerRepaint?.();
    // --- END UPDATE
  }, []);

  const selectObject = useCallback((object, data, options = {}) => {
    if (highlightedObjectRef.current && highlightedObjectRef.current !== object) {
      clearSelection();
    }
    highlightedObjectRef.current = object;
    if (!object.isInstancedMesh) {
      applyEmissive(object, SELECTION_COLOR);
      const baseScale = object.userData.originalScale || 1;
      object.scale.setScalar(baseScale * SELECT_SCALE);
    }
    setSelectedAsset(data);
    const { flyToOnSelect = true, flyOptions = {} } = options;
    if (flyToOnSelect && data?.longitude != null && data?.latitude != null && mapRef.current) {
      const map = mapRef.current;
      map.flyTo({
        center:  [data.longitude, data.latitude],
        zoom:    Math.max(map.getZoom(), 17),
        speed:   0.8,
        curve:   1.4,
        pitch:   60,
        bearing: map.getBearing(),
        essential: true,
        ...flyOptions,
      });
    }
  }, [clearSelection]);

  // ============================================================================
  // WEBXR AUGMENTED REALITY (AR) LIFECYCLE
  // Catches the AR launch event dispatched by AugmentedRealityButton,
  // detaches scene groups from Mapbox world space, creates a tabletop hologram,
  // and cleanly restores everything when the session ends.
  // ============================================================================
  useEffect(() => {
    const handleStartAR = async (e) => {
      const session = e?.detail?.session;
      const three   = threeRef.current;

      // Guard: Three.js must be fully initialised before AR can start
      if (!three || !three.renderer || !three.scene) {
        logger.error('[WebXR] Three.js not initialised — cannot start AR session.');
        return;
      }
      if (!session) {
        logger.error('[WebXR] START_AR_SESSION fired without a valid XRSession in event.detail.session.');
        return;
      }
      if (arSessionActiveRef.current) {
        logger.warn('[WebXR] AR session already active — ignoring duplicate START_AR_SESSION.');
        return;
      }

      const { renderer, scene } = three;

      try {
        // 1. Enable WebXR on the Three.js renderer and hand it the live XRSession
        renderer.xr.enabled = true;
        await renderer.xr.setSession(session);
        arSessionActiveRef.current = true;
        logger.log('[WebXR] AR session started successfully.');
      } catch (err) {
        logger.error('[WebXR] Failed to set XR session on renderer:', err);
        renderer.xr.enabled = false;
        arSessionActiveRef.current = false;
        return;
      }

      // 2. Create (or reuse) a dedicated AR container group
      let arGroup = scene.getObjectByName('AR_CONTAINER');
      if (!arGroup) {
        arGroup = new THREE.Group();
        arGroup.name = 'AR_CONTAINER';
        scene.add(arGroup);
      }

      // 3. Snapshot original parents so we can put everything back on session end
      const originalParents = {};
      AR_REPARENTED_GROUPS.forEach(name => {
        const grp = scene.getObjectByName(name);
        if (grp) {
          // Detach from whatever parent it currently has (usually the scene root)
          originalParents[name] = grp.parent;
          arGroup.add(grp); // THREE automatically removes from previous parent
        }
      });

      // 4. Scale the Mercator-space city coordinates down to a tabletop hologram
      arGroup.scale.setScalar(AR_HOLOGRAM_SCALE);

      // 5. Position 1.5 m ahead and 0.5 m below the user's eye level
      arGroup.position.set(
        AR_HOLOGRAM_POSITION.x,
        AR_HOLOGRAM_POSITION.y,
        AR_HOLOGRAM_POSITION.z,
      );

      // 6. Rotate flat so it reads as a map on a table surface
      arGroup.rotation.x = AR_HOLOGRAM_ROTATION_X;

      // 7. Restore scene graph and disable XR when the technician ends the session
      const handleSessionEnd = () => {
        logger.log('[WebXR] AR session ended — restoring scene graph.');

        try {
          // Return every group to its original parent
          AR_REPARENTED_GROUPS.forEach(name => {
            const grp = scene.getObjectByName(name);
            if (grp && originalParents[name]) {
              originalParents[name].add(grp); // re-parents & removes from arGroup
            }
          });

          // Reset the AR container transform so it is neutral if reused
          arGroup.scale.setScalar(1);
          arGroup.position.set(0, 0, 0);
          arGroup.rotation.set(0, 0, 0);
          scene.remove(arGroup);
        } catch (cleanupErr) {
          logger.error('[WebXR] Error restoring scene graph after AR session:', cleanupErr);
        } finally {
          renderer.xr.enabled = false;
          arSessionActiveRef.current = false;
        }
      };

      // 'once: true' ensures cleanup runs exactly once even if 'end' fires multiple times
      session.addEventListener('end', handleSessionEnd, { once: true });
    };

    window.addEventListener('START_AR_SESSION', handleStartAR);

    return () => {
      window.removeEventListener('START_AR_SESSION', handleStartAR);
    };
    // This effect has no React state dependencies — it reads threeRef at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== MAP & THREE.JS INITIALISATION ====================
  const initialize = useCallback((onAssetClick, options = {}) => {
    if (!containerRef.current || mapRef.current) return () => {};

    const { openOnClick = false, flyToOnSelect = true, flyOptions = {}, renderOnDemand = false } = options;
    // --- UPDATE
    renderOnDemandRef.current = !!renderOnDemand;
    // --- END UPDATE

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style:     'mapbox://styles/mapbox/dark-v11',
      center:    [-77.355, 25.047],
      zoom:      15.5,
      pitch:     65,
      bearing:   -17.6,
      antialias: true,
    });
    mapRef.current = map;
    // --- UPDATE
    if (isMountedRef.current) setMapInstance(map);
    // --- END UPDATE

    const raycaster = new THREE.Raycaster();
    raycaster.params.Line.threshold = 0.0005;
    const pointer = new THREE.Vector2();

    const setupRaycaster = (pt) => {
      const three = threeRef.current;
      if (!three?.camera) return;
      const camInverse = three.camera.projectionMatrixInverse;
      const origin    = new THREE.Vector3(pt.x, pt.y, -1).applyMatrix4(camInverse);
      const target    = new THREE.Vector3(pt.x, pt.y,  1).applyMatrix4(camInverse);
      const direction = target.clone().sub(origin).normalize();
      raycaster.set(origin, direction);
    };

    const handlePointerMove = (evt) => {
      const now = Date.now();
      if (now - lastMouseMoveRef.current < MOUSE_THROTTLE_MS) return;
      lastMouseMoveRef.current = now;

      const canvas = map.getCanvas();
      pointer.x =  (evt.point.x / canvas.clientWidth)  * 2 - 1;
      pointer.y = -(evt.point.y / canvas.clientHeight) * 2 + 1;
      setupRaycaster(pointer);

      const intersects = raycaster.intersectObjects(raycastCacheRef.current, true);

      if (intersects.length > 0) {
        const hit = intersects[0];
        let obj, sourceData;

        if (hit.object.isInstancedMesh && hit.instanceId !== undefined) {
          obj        = hit.object;
          sourceData = obj.userData.sensorLookup?.[hit.instanceId]?.sourceData;
        } else {
          obj        = findRoot(hit.object);
          sourceData = obj?.userData?.sourceData;
        }

        if (obj && !obj.isInstancedMesh && hoveredObjectRef.current !== obj && obj !== highlightedObjectRef.current) {
          if (hoveredObjectRef.current && hoveredObjectRef.current !== highlightedObjectRef.current) {
            const hoverBaseScale = hoveredObjectRef.current.userData.originalScale || 1;
            hoveredObjectRef.current.scale.setScalar(hoverBaseScale);
            restoreEmissive(hoveredObjectRef.current);
          }
          hoveredObjectRef.current = obj;
          applyEmissive(obj, HOVER_COLOR);
          const objBaseScale = obj.userData.originalScale || 1;
          obj.scale.setScalar(objBaseScale * HOVER_SCALE);
        }

        if (sourceData) {
          map.getCanvas().style.cursor = 'pointer';
          // --- UPDATE: only re-render the page when the hovered item changes
          const key = hit.object.isInstancedMesh ? `s:${hit.instanceId}` : `o:${obj?.uuid}`;
          if (hoveredKeyRef.current !== key) {
            hoveredKeyRef.current = key;
            setHoveredAsset(sourceData);
          }
          // --- END UPDATE
        }
        map.triggerRepaint();
      } else {
        if (hoveredObjectRef.current && hoveredObjectRef.current !== highlightedObjectRef.current) {
          if (!hoveredObjectRef.current.isInstancedMesh) {
            const hoverBaseScale = hoveredObjectRef.current.userData.originalScale || 1;
            hoveredObjectRef.current.scale.setScalar(hoverBaseScale);
            restoreEmissive(hoveredObjectRef.current);
          }
          hoveredObjectRef.current = null;
        }
        // --- UPDATE
        if (hoveredKeyRef.current !== null) {
          hoveredKeyRef.current = null;
          setHoveredAsset(null);
          map.triggerRepaint();
        }
        // --- END UPDATE
        map.getCanvas().style.cursor = '';
      }
    };

    const handleMapClick = (event) => {
      const canvas = map.getCanvas();
      const mouse  = new THREE.Vector2(
        (event.point.x / canvas.clientWidth)  * 2 - 1,
        -(event.point.y / canvas.clientHeight) * 2 + 1,
      );
      setupRaycaster(mouse);
      const intersects = raycaster.intersectObjects(raycastCacheRef.current, true);

      if (intersects.length === 0) {
        clearSelection();
        onAssetClick?.(null);
        return;
      }

      const hit = intersects[0];
      let picked, data;

      if (hit.object.isInstancedMesh && hit.instanceId !== undefined) {
        picked = hit.object;
        data   = picked.userData.sensorLookup?.[hit.instanceId]?.sourceData;
        // --- UPDATE: copy instead of mutating the source object held in React state
        if (data) data = { ...data, isSensor: true };
        // --- END UPDATE
        setSelectedAsset(data);
      } else {
        picked = findRoot(hit.object);
        data   = picked?.userData?.sourceData;
        // --- UPDATE
        if (data) data = { ...data, isAsset: true };
        // --- END UPDATE
        if (picked) selectObject(picked, data, { flyToOnSelect, flyOptions });
      }

      onAssetClick?.(data, { openTwinDrawer: true, openFullModal: openOnClick });
    };

    const handleResize = () => {
      if (!containerRef.current || !threeRef.current.renderer) return;
      const canvas = map.getCanvas();
      // --- UPDATE: setSize() also rewrote the shared canvas' CSS size in DEVICE
      // pixels (2× too large on HiDPI) and reset its buffer. Only the viewport
      // three.js renders into needs to follow the map.
      threeRef.current.renderer.setViewport(0, 0, canvas.width, canvas.height);
      map.triggerRepaint();
      // --- END UPDATE
    };

    map.on('load', () => {
      const customLayer = {
        id:            'threejs-layer',
        type:          'custom',
        renderingMode: '3d',

        onAdd: (_map, gl) => {
          const three = threeRef.current;

          three.camera = new THREE.PerspectiveCamera();
          three.camera.matrixAutoUpdate = false;

          three.scene = new THREE.Scene();
          ensureSceneGroups();

          const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
          three.scene.add(ambientLight);

          const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
          directionalLight.position.set(0, -1, 1).normalize();
          three.scene.add(directionalLight);

          const hemisphereLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 0.3);
          three.scene.add(hemisphereLight);

          three.renderer = new THREE.WebGLRenderer({
            canvas:    map.getCanvas(),
            context:   gl,
            antialias: true,
            alpha:     true,
          });
          three.renderer.autoClear = false;
          // --- UPDATE: match the map's drawing buffer from the first frame
          three.renderer.setViewport(0, 0, map.getCanvas().width, map.getCanvas().height);
          // --- END UPDATE

          three.origin = MercatorCoordinate.fromLngLat(map.getCenter());

          if (isMountedRef.current) {
            setScene(three.scene);
            setOrigin(three.origin);
            setIsReady(true);
          }

          map.on('mousemove', handlePointerMove);
          map.on('click',     handleMapClick);
          map.on('resize',    handleResize);
        },

        render: (_gl, matrix) => {
          const three = threeRef.current;
          if (!three.renderer || !three.origin) return;

          // Skip the Mapbox render path while WebXR is driving the frame loop
          if (arSessionActiveRef.current) return;

          animationService.update();

          // --- UPDATE: reuse scratch objects (no per-frame allocation)
          const scratch = frameScratch.current;
          const m = scratch.m.fromArray(matrix);
          const translation = scratch.t.makeTranslation(
            three.origin.x,
            three.origin.y,
            three.origin.z,
          );
          three.camera.projectionMatrix.copy(m).multiply(translation);
          // --- END UPDATE

          try {
            three.camera.projectionMatrixInverse.copy(three.camera.projectionMatrix).invert();
          } catch {
            three.camera.projectionMatrixInverse.getInverse(three.camera.projectionMatrix);
          }

          // DYNAMIC SENSOR VISUALS FROM WEBSOCKETS
          // --- UPDATE: colours + link propagation only when telemetry changed;
          // per-frame work is limited to pulsing the critical sensors.
          const sensorsMesh = three.sensorsGroup?.getObjectByName('sensor-instances');
          if (sensorsMesh?.userData?.sensorLookup) {
            const meterInUnits = three.origin.meterInMercatorCoordinateUnits();
            const baseScale    = 3 * meterInUnits;
            const dummy        = scratch.dummy;
            const tempColor    = scratch.color;

            if (appliedVersionRef.current !== readingsVersionRef.current) {
              appliedVersionRef.current = readingsVersionRef.current;
              let colorsChanged = false;
              let matricesChanged = false;
              const critical = [];

              // Lazy id → object lookups (O(1) instead of children.find per sensor)
              if (!pipelineObjectsRef.current) {
                const pm = new Map();
                (three.pipelinesGroup?.children || []).forEach(c => {
                  if (c.userData?.pipelineId != null) pm.set(String(c.userData.pipelineId), c);
                });
                pipelineObjectsRef.current = pm;
              }

              sensorsMesh.userData.sensorLookup.forEach((lookup, i) => {
                if (!lookup) return;
                const sensor  = lookup.sourceData;
                const reading = liveReadingsRef.current[sensor.device_id || sensor.id];

                let isCritical     = false;
                let targetColorHex = 0x00aaff; // Default: blue

                if (reading) {
                  const val = reading.psi ?? reading.level ?? reading.value;
                  if (val !== undefined) {
                    if (sensor.type === 'water_level') {
                      if (val === 'HIGH' || val > 85)   { isCritical = true; targetColorHex = 0xffa500; }
                      else if (val === 'EMPTY' || val < 10) targetColorHex = 0xef4444;
                      else targetColorHex = 0x00dd00;
                    } else {
                      if (val > (sensor.max_threshold || 45) || val < (sensor.min_threshold || 15)) {
                        isCritical = true; targetColorHex = 0xff0000;
                      } else {
                        targetColorHex = 0x00dd00;
                      }
                    }
                  }
                }

                sensorsMesh.getColorAt(i, tempColor);
                if (tempColor.getHex() !== targetColorHex) {
                  sensorsMesh.setColorAt(i, tempColor.setHex(targetColorHex));
                  colorsChanged = true;
                }
                if (isCritical) critical.push(i);
                else if (lookup.wasCritical) {
                  // restore base size once when a sensor leaves the critical state
                  sensorsMesh.getMatrixAt(i, dummy.matrix);
                  dummy.position.setFromMatrixPosition(dummy.matrix);
                  dummy.scale.setScalar(baseScale);
                  dummy.updateMatrix();
                  sensorsMesh.setMatrixAt(i, dummy.matrix);
                  matricesChanged = true;
                }
                lookup.wasCritical = isCritical;

                // ==============================================================
                // --- LINK PROPAGATION ENGINE ---
                // If the sensor changes color, force the linked Pipeline/Asset to match
                // ==============================================================
                const sId = sensor.device_id || sensor.id;
                const linkedPipelineId = sensorLinksRef.current[sId];
                const linkedAssetId = sensor.linked_entity_id;
                const targetId = linkedPipelineId || linkedAssetId;
                if (!targetId) return;

                const linkedMesh = linkedPipelineId
                  ? pipelineObjectsRef.current.get(String(linkedPipelineId))
                  : assetObjectsRef.current.get(String(linkedAssetId));
                // Simulated objects keep their simulated glow
                if (!linkedMesh || linkedMesh.userData.simulationState) return;

                const newStatus = isCritical ? 'critical' : (targetColorHex === 0xffa500 ? 'warning' : 'normal');

                // Only traverse and update materials if the status ACTUALLY changed (Performance optimization)
                if (linkedMesh.userData.currentLiveStatus !== newStatus) {
                  linkedMesh.userData.currentLiveStatus = newStatus;

                  linkedMesh.traverse(child => {
                    if (child.isMesh && child.material) {
                      const processMat = (mat) => {
                        // Save original colors if we haven't already
                        if (mat.userData.baseEmissiveHex === undefined) {
                          mat.userData.baseEmissiveHex = mat.emissive ? mat.emissive.getHex() : 0x000000;
                          mat.userData.baseEmissiveIntensity = mat.emissiveIntensity || 0;
                          mat.userData.baseOpacity = mat.opacity || 1.0;
                        }

                        if (newStatus === 'critical') {
                          if (!mat.emissive) mat.emissive = new THREE.Color(0);
                          mat.emissive.setHex(0xff0000);
                          mat.emissiveIntensity = 0.8;
                          mat.opacity = 1.0;
                          mat.transparent = false;
                        } else if (newStatus === 'warning') {
                          if (!mat.emissive) mat.emissive = new THREE.Color(0);
                          mat.emissive.setHex(0xffa500);
                          mat.emissiveIntensity = 0.8;
                          mat.opacity = 1.0;
                          mat.transparent = false;
                        } else {
                          // Revert to original
                          if (mat.emissive) mat.emissive.setHex(mat.userData.baseEmissiveHex);
                          mat.emissiveIntensity = mat.userData.baseEmissiveIntensity;
                          mat.opacity = mat.userData.baseOpacity;
                          mat.transparent = mat.opacity < 1.0;
                        }
                        mat.needsUpdate = true;
                      };
                      Array.isArray(child.material) ? child.material.forEach(processMat) : processMat(child.material);
                    }
                  });
                }
              });

              criticalSensorIdxRef.current = critical;
              if (colorsChanged && sensorsMesh.instanceColor) sensorsMesh.instanceColor.needsUpdate = true;
              if (matricesChanged) sensorsMesh.instanceMatrix.needsUpdate = true;
            }

            // Per-frame: pulse only the critical sensors
            const critical = criticalSensorIdxRef.current;
            if (critical.length) {
              const timePhase = Date.now() * 0.005;
              for (let k = 0; k < critical.length; k += 1) {
                const i = critical[k];
                sensorsMesh.getMatrixAt(i, dummy.matrix);
                dummy.position.setFromMatrixPosition(dummy.matrix);
                dummy.scale.setScalar(baseScale * (Math.sin(timePhase + i) * 0.3 + 1.3));
                dummy.updateMatrix();
                sensorsMesh.setMatrixAt(i, dummy.matrix);
              }
              sensorsMesh.instanceMatrix.needsUpdate = true;
            }
          }
          // --- END UPDATE

          three.renderer.resetState();
          three.renderer.render(three.scene, three.camera);
          // --- UPDATE: on-demand mode keeps repainting only while something animates
          if (!renderOnDemandRef.current || pulsingCountRef.current > 0 || criticalSensorIdxRef.current.length > 0) {
            map.triggerRepaint();
          }
          // --- END UPDATE
        },
      };

      map.addLayer(customLayer);
    });

    return () => {
      try {
        map.off('mousemove', handlePointerMove);
        map.off('click',     handleMapClick);
        map.off('resize',    handleResize);
        map.remove();
      } catch (err) {
        logger.error('[DT_HOOK] Map cleanup error:', err);
      } finally {
        mapRef.current = null;
        if (isMountedRef.current) {
          setScene(null);
          setOrigin(null);
          setIsReady(false);
          // --- UPDATE
          setMapInstance(null);
          // --- END UPDATE
        }
      }
    };
  }, [containerRef, ensureSceneGroups, findRoot, updateRaycastCache, clearSelection, selectObject]);

  // ==================== POPULATE SCENE ====================
  const populateScene = useCallback(async (assets = [], pipelines = [], sensors = [], healthMap = {}) => {
    const three       = threeRef.current;
    const populateId  = ++currentPopulateIdRef.current;

    if (!three?.assetsGroup || !three?.pipelinesGroup || !three?.sensorsGroup || !three?.origin) return;

    three.assetsGroup.children.forEach(disposeObject);
    three.sensorsGroup.children.forEach(disposeObject);
    three.assetsGroup.clear();
    three.sensorsGroup.clear();
    // --- UPDATE
    assetObjectsRef.current = new Map();
    pulsingCountRef.current = 0;
    criticalSensorIdxRef.current = [];
    // --- END UPDATE

    // --- 1. ASSETS ---
    // --- UPDATE (twin v8): progress for the loading UI + progressive repaint, so assets appear while
    // the rest load (the page used to stay blank until the last model was placed)
    const placeable = assets.filter((a) => a && a.longitude != null && a.latitude != null);
    const progress = { total: placeable.length, loaded: 0, failed: 0, startedAt: performance.now() };
    let lastReport = 0;
    const report = (force = false) => {
      const t = performance.now();
      if (!force && t - lastReport < 250) return;
      lastReport = t;
      if (isMountedRef.current && populateId === currentPopulateIdRef.current) {
        setSceneProgress({ total: progress.total, loaded: progress.loaded, failed: progress.failed, done: false, ms: Math.round(t - progress.startedAt) });
      }
      mapRef.current?.triggerRepaint?.();
    };
    report(true);
    // --- END UPDATE
    // --- UPDATE: bounded-concurrency loading (was one await per asset in sequence)
    const buildAsset = async (asset) => {
      if (populateId !== currentPopulateIdRef.current) return;
      if (asset.longitude == null || asset.latitude == null) return;

      try {
        const model = await modelLoaderService.loadModel(asset.model_url);
        if (populateId !== currentPopulateIdRef.current || !model) return;

        const health     = healthMap?.[asset.id];
        const normHealth = health?.health_status?.toLowerCase().replace(/\s+/g, '_');
        const normStatus = asset.status?.toLowerCase().replace(/\s+/g, '_');

        const isPhysicalCritical = ['damaged', 'critical', 'offline'].includes(normStatus);

        let targetColor;
        if (isPhysicalCritical && statusColors[normStatus])    targetColor = statusColors[normStatus];
        else if (normHealth && healthStatusColors[normHealth]) targetColor = healthStatusColors[normHealth];
        else if (normStatus && statusColors[normStatus])       targetColor = statusColors[normStatus];
        else                                                   targetColor = statusColors.unknown;

        const isCritical = isPhysicalCritical || normHealth === 'critical' || normHealth === 'warning';

        model.traverse(child => {
          if (child.isMesh) {
            child.raycast = THREE.Mesh.prototype.raycast;
            if (child.geometry) {
              if (!child.geometry.boundingBox)    child.geometry.computeBoundingBox();
              if (!child.geometry.boundingSphere) child.geometry.computeBoundingSphere();
            }
            if (child.material) {
              child.material = Array.isArray(child.material)
                ? child.material.map(m => m.clone())
                : child.material.clone();

              const applyTint = (mat) => {
                mat.transparent = true;
                if (mat.color) mat.color.lerp(targetColor, 0.85);
                else           mat.color = targetColor.clone();
                if (isCritical) {
                  if (!mat.emissive) mat.emissive = new THREE.Color(0x000000);
                  mat.emissive.copy(targetColor);
                  mat.emissiveIntensity = 0.6;
                }
                mat.userData = mat.userData || {};
                mat.userData.baseOpacity           = mat.opacity ?? 1.0;
                mat.userData.baseEmissiveIntensity = mat.emissiveIntensity || 0.0;
                mat.userData.baseEmissiveHex       = mat.emissive ? mat.emissive.getHex() : 0x000000;
              };

              if (Array.isArray(child.material)) child.material.forEach(applyTint);
              else applyTint(child.material);
            }
          }
        });

        model.updateMatrixWorld(true);
        if (isCritical) { addPulsingAnimation(model); pulsingCountRef.current += 1; }

        const mc    = MercatorCoordinate.fromLngLat([asset.longitude, asset.latitude]);
        const scale = mc.meterInMercatorCoordinateUnits() * ASSET_SCALE_MULTIPLIER;

        model.position.set(mc.x - three.origin.x, mc.y - three.origin.y, 0);
        model.scale.set(scale, scale, scale);
        model.rotation.set(Math.PI / 2, 0, 0);

        model.userData = {
          type:          'asset',
          assetId:       asset.id,
          sourceData:    asset,
          originalScale: scale,
        };
        model.name = `asset-${asset.id}`;
        if (populateId !== currentPopulateIdRef.current) { disposeObject(model); return; }
        three.assetsGroup.add(model);
        assetObjectsRef.current.set(String(asset.id), model);
        progress.loaded += 1; report(); // --- UPDATE (twin v8)
      } catch (err) {
        progress.failed += 1; // --- UPDATE (twin v8)
        logger.error(`populateScene: asset ${asset.id} failed`, err);
      }
    };

    const queue = [...placeable]; // --- UPDATE (twin v8): only assets that can be placed
    const workers = Array.from({ length: Math.min(MODEL_LOAD_CONCURRENCY, queue.length) }, async () => {
      while (queue.length && populateId === currentPopulateIdRef.current) {
        // eslint-disable-next-line no-await-in-loop
        await buildAsset(queue.shift());
      }
    });
    await Promise.all(workers);
    if (populateId !== currentPopulateIdRef.current) return;
    // --- END UPDATE

    // --- 2. SENSORS (Instanced Mesh) ---
    // --- UPDATE: sensors without coordinates are excluded (they left identity
    // matrices → phantom spheres at the scene origin)
    const placedSensors = sensors.filter(sn => sn.longitude != null && sn.latitude != null
      && Number.isFinite(Number(sn.longitude)) && Number.isFinite(Number(sn.latitude)));
    // --- END UPDATE
    if (placedSensors.length > 0) {
      const meterInUnits = three.origin.meterInMercatorCoordinateUnits();

      const sensorGeometry = new THREE.SphereGeometry(1, 16, 16);
      const sensorMaterial = new THREE.MeshPhysicalMaterial({
        color:     0xffffff,
        metalness: 0.3,
        roughness: 0.2,
        clearcoat: 1.0,
      });

      const instancedMesh = new THREE.InstancedMesh(sensorGeometry, sensorMaterial, placedSensors.length);
      instancedMesh.name  = 'sensor-instances';
      instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

      const dummy        = new THREE.Object3D();
      const color        = new THREE.Color();
      const sensorLookup = [];

      placedSensors.forEach((sensor, i) => {
        const mc = MercatorCoordinate.fromLngLat([Number(sensor.longitude), Number(sensor.latitude)]);
        dummy.position.set(mc.x - three.origin.x, mc.y - three.origin.y, 5 * meterInUnits);
        dummy.scale.setScalar(3 * meterInUnits);
        dummy.updateMatrix();
        instancedMesh.setMatrixAt(i, dummy.matrix);

        const normSensorStatus = sensor.status?.toLowerCase().replace(/\s+/g, '_');
        const statusColor = (normSensorStatus && statusColors[normSensorStatus])
          ? statusColors[normSensorStatus].getHex()
          : 0x00aaff;

        instancedMesh.setColorAt(i, color.setHex(statusColor));

        sensorLookup[i] = {
          type:     'sensor',
          sensorId: sensor.device_id || sensor.id,
          sourceData: sensor,
        };
      });

      instancedMesh.instanceMatrix.needsUpdate = true;
      if (instancedMesh.instanceColor) instancedMesh.instanceColor.needsUpdate = true;

      instancedMesh.userData = { type: 'sensor-group', sensorLookup };
      three.sensorsGroup.add(instancedMesh);
    }

    updateRaycastCache();
    // --- UPDATE (twin v8)
    if (isMountedRef.current && populateId === currentPopulateIdRef.current) {
      setSceneProgress({ total: progress.total, loaded: progress.loaded, failed: progress.failed, done: true, ms: Math.round(performance.now() - progress.startedAt) });
    }
    // --- END UPDATE
    // --- UPDATE: re-apply live telemetry to the new meshes and draw
    appliedVersionRef.current = -1;
    mapRef.current?.triggerRepaint?.();
    // --- END UPDATE
  }, [updateRaycastCache]);

  const setGroupVisibility = useCallback((groupName, isVisible) => {
    const group = threeRef.current?.scene?.getObjectByName(groupName);
    if (group) group.visible = isVisible;
    // --- UPDATE
    mapRef.current?.triggerRepaint?.();
    // --- END UPDATE
  }, []);

  const flyTo = useCallback((longitude, latitude, options = {}) => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({
      center:   [longitude, latitude],
      zoom:     16,
      speed:    0.8,
      curve:    1.4,
      pitch:    60,
      essential: true,
      ...options,
    });
  }, []);

  const resetCamera = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({
      center:  [-77.355, 25.047],
      zoom:    15.5,
      pitch:   65,
      bearing: -17.6,
      speed:   1,
      essential: true,
    });
  }, []);

  const getPerformanceStats = useCallback(() => {
    const three = threeRef.current;
    if (!three.renderer) return null;
    return {
      memory:   three.renderer.info.memory,
      render:   three.renderer.info.render,
      programs: three.renderer.info.programs?.length || 0,
    };
  }, []);

  return useMemo(() => ({
    initialize,
    populateScene,
    setGroupVisibility,
    flyTo,
    resetCamera,
    clearSelection,
    selectObject,
    updateLiveReadings,
    selectedAsset,
    hoveredAsset,
    scene,
    origin,
    isReady,
    getPerformanceStats,
    // --- UPDATE
    map:              mapInstance,
    requestRender,
    metersToScene,
    getAssetObject,
    // --- END UPDATE
    three:            threeRef.current,
    updateRaycastCache,
    sceneProgress, // --- UPDATE (twin v8)
  }), [
    initialize,
    populateScene,
    setGroupVisibility,
    flyTo,
    resetCamera,
    clearSelection,
    selectObject,
    updateLiveReadings,
    getPerformanceStats,
    scene,
    origin,
    selectedAsset,
    hoveredAsset,
    isReady,
    updateRaycastCache,
    // --- UPDATE
    mapInstance,
    requestRender,
    metersToScene,
    getAssetObject,
    // --- END UPDATE
    sceneProgress, // --- UPDATE (twin v8)
  ]);
}